import {Component, Material, Mesh, MeshComponent, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {quat, vec3} from 'gl-matrix';
import {ParticleMaterial} from '../types/materials.js';

/**
Very simple smoke particles system
*/
export class Particles extends Component {
    static TypeName = 'particles';
    //static Properties = {

    /* Mesh for spawned particles */
    @property.mesh()
    mesh?: Mesh;

    /* Material for spawned particles */
    @property.material()
    material?: Material;

    /* Delay between particle spawns. If below time of a frame, will spawn multiple particles in update. */
    @property.float(0.1)
    delay = 0.1;

    /* Maximum number of particles, once limit is reached, particles are recycled first-in-first-out. */
    @property.int(64)
    maxParticles = 64;

    /* Initial speed of emitted particles. */
    @property.float(30)
    initialSpeed = 30;

    /* MB Editions */

    /* Particle Scale */
    @property.float(0.25)
    scale = 0.25;

    @property.float(1)
    direction = 1; /* [1]Backward, [1]Up, [2]Forward */

    @property.bool(false)
    directionInverse = false; /* Inverse Direction */

    private time = 0.0;
    private timeSinceSpawn = 0.0;
    private count = 0;
    private objects?: Object3D[];
    private materials: ParticleMaterial[] = [];
    private velocities: vec3[] = [];
    private rotationFactor?: Float32Array;
    private startTime?: Float32Array;
    private particleActive = false;
    private origin = vec3.create();
    private vel = vec3.create();
    private viewPos = vec3.create();

    // };

    // init() {
    //     this.time = 0.0;
    //     this.timeSinceSpawn = 0.0;
    //     this.count = 0;
    // }

    start() {
        this.objects = this.engine.scene.addObjects(this.maxParticles, undefined, 1);
        // this.materials = [];
        // this.velocities = [];
        this.rotationFactor = new Float32Array(this.maxParticles);
        this.startTime = new Float32Array(this.maxParticles);
        this.particleActive = true;

        /* Temporary variables for math, allocated once to avoid garbage collection */
        // this.origin = [0, 0, 0];
        // this.vel = [0, 0, 0];
        // this.viewPos = [0, 0, 0];
    }

    update(dt: number) {
        if (!this.particleActive) return;

        this.time += dt;
        this.timeSinceSpawn += dt;
        if (this.timeSinceSpawn > this.delay) {
            /* Time to spawn particles */
            const spawnCount = Math.floor(this.timeSinceSpawn / this.delay);
            for (let i = 0; i < spawnCount; ++i) this.spawn();
            this.timeSinceSpawn -= this.delay * spawnCount;
        }

        const origin = this.origin;
        const vel = this.vel;
        const viewPos = this.viewPos;

        this.object.getPositionWorld(origin);
        /* Target for retrieving particles world locations */
        this.engine.scene.activeViews[0].object.getPositionWorld(viewPos);

        vec3.sub(viewPos, origin, viewPos);
        viewPos[1] = 0;
        vec3.normalize(viewPos, viewPos);
        const rotation = quat.rotationTo([], [0, 0, 1], viewPos) as quat;

        const col = [1, 1, 1, 1];

        for (let i = 0; i < this.velocities.length; ++i) {
            const lifeTime = this.time - this.startTime![i];
            const obj = this.objects![i];
            vec3.scale(vel, this.velocities[i], dt);
            obj.getPositionWorld(origin);
            /* Apply velocity */
            vec3.add(origin, origin, vel);

            obj.resetTransform();
            const s = this.scale + this.scale * lifeTime;
            obj.scaleLocal([s, s, s]);
            obj.rotateLocal(rotation);
            obj.setPositionLocal(origin);

            this.materials[i].offsetX = 0.1 * lifeTime + 100 * this.rotationFactor![i];

            const f = 0.2 * lifeTime;
            col[0] = col[1] = col[2] = 1 - f * f;
            this.materials[i].color = col;
        }

        /* drag */
        for (let i = 0; i < this.velocities.length; ++i) {
            if (!this.directionInverse) {
                this.velocities[i][this.direction] += dt; /* Direction */
            } else {
                this.velocities[i][this.direction] -= dt; /* Direction */
            }
            vec3.scale(this.velocities[i], this.velocities[i], 1.0 - 0.5 * dt);
        }
    }

    /** Spawn a particle */
    spawn() {
        const index = this.count % this.maxParticles;
        const obj = this.objects![index];
        /* If we are not at this.maxParticles yet, spawn a new object */
        if (index >= this.velocities.length) {
            this.velocities.push([0, 0, 0]);

            obj.name = 'particle' + this.count.toString();
            const mesh = obj.addComponent('mesh', {
                mesh: this.mesh,
                material: this.material!.clone(),
            });
            this.materials.push(mesh!.material as ParticleMaterial);
        }
        obj.setTransformWorld(this.object.getTransformWorld());
        obj.setScalingLocal([0, 0, 0]);

        this.startTime![index] = this.time;
        this.rotationFactor![index] = Math.random();
        this.velocities[index][0] = 2.0 * (Math.random() - 0.5);
        this.velocities[index][1] = Math.random() * 3.0 + 3.0;
        this.velocities[index][2] = 2.0 * (Math.random() - 0.5) + 2.0;

        vec3.normalize(this.velocities[index], this.velocities[index]);
        vec3.scale(this.velocities[index], this.velocities[index], this.initialSpeed);

        this.count += 1;
    }

    /* Call it then deactive the component to stop particles */
    endParticles() {
        for (let i = 0; i < this.objects!.length; ++i) {
            const obj = this.objects![i];
            if (obj.getComponent(MeshComponent)) {
                obj.getComponent(MeshComponent)!.active =
                    false; /* Remove the mesh component if present */
            }
            obj.active = false; /* Deactivate the particle object */
        }

        /* Clear arrays and reset counters */
        this.objects = [];
        this.materials = [];
        this.velocities = [];
        this.rotationFactor = new Float32Array(this.maxParticles);
        this.startTime = new Float32Array(this.maxParticles);
        this.count = 0;
        this.particleActive = false;
    }

    startParticles() {
        this.particleActive = true;
    }
}
